export const domParser = (data: string) => {
  const parser = new DOMParser();
  const doc: any = parser.parseFromString(data, "text/html");

  // Lấy toàn bộ nội dung text trong <body>, bao gồm cả text ngoài <p>
  const text = doc.body.innerHTML;
  return text || "";
};

export const extractCourseGeneralContents = (html: string) => {
  // Tạo DOM từ chuỗi HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Tìm thẻ có class card-body
  const cardBody = doc.querySelector(".card-body");

  // Trả về cả thẻ <div class="card-body">...</div>
  return cardBody ? cardBody.outerHTML : "";
};

export type ExtractedMaterial = {
  type: string;
  text: string;
  url: string;
};

export type ExtractedUnilabel = {
  title: string | null;
  items: ExtractedMaterial[];
};

const extractUnilabelTitle = (doc: Document): string | null => {
  const noOverflowBlocks = Array.from(doc.querySelectorAll("div.no-overflow"));

  for (const block of noOverflowBlocks) {
    if (block.querySelector(".eea-lession")) continue;

    const strong = block.querySelector(":scope > strong") || block.querySelector("strong");
    const title = strong?.textContent?.trim();
    if (title) return title;
  }

  const fallbackStrong = doc.querySelector("div.no-overflow > strong");
  const fallbackTitle = fallbackStrong?.textContent?.trim();
  return fallbackTitle || null;
};

export const extractTextAndUrls = (htmlString: string): ExtractedUnilabel => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString || "", "text/html");

  const items: ExtractedMaterial[] = [];
  const title = extractUnilabelTitle(doc);

  const lessons = doc.querySelectorAll(".eea-lession");

  lessons.forEach((lesson) => {
    const link = lesson.querySelector("a[data-src]");

    const url = link?.getAttribute("data-src") || "";
    const type = link?.getAttribute("data-type") || "unknown";

    // Lấy nội dung text từ <p>
    let textContent = "";
    const paragraphs = lesson.querySelectorAll("p");
    paragraphs.forEach((p) => {
      const text = p.textContent?.trim();
      if (text) {
        textContent += (textContent ? " " : "") + text;
      }
    });

    // Nếu không có <p>, lấy text trực tiếp
    if (!textContent) {
      const allTextNodes = lesson.childNodes;
      allTextNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          textContent += (textContent ? " " : "") + node.textContent.trim();
        }
      });
    }

    if (url || textContent) {
      items.push({ type, text: textContent, url });
    }
  });

  return { title, items };
};

type QuizAnswer = {
  text: string;
  name: string;
  value: string;
};

type QuizQuestion = {
  question: string;
  questionImageSrcs: string[];
  answers: QuizAnswer[];
  selectedAnswerIndex: number | null;
  sequenceCheck: {
    name: string;
    value: string;
  } | null;
};

const normalizeMoodleFileUrl = (src: string): string => {
  if (!src) return "";

  const trimmedSrc = src.trim();
  if (trimmedSrc.startsWith("http://") || trimmedSrc.startsWith("https://")) {
    return trimmedSrc;
  }

  const baseUrl = (process.env.REACT_APP_MOODLE_API || "").replace(/\/$/, "");
  return trimmedSrc.startsWith("/")
    ? `${baseUrl}${trimmedSrc}`
    : `${baseUrl}/${trimmedSrc}`;
};

export const isPluginFileUrl = (src: string): boolean =>
  src.includes("/pluginfile.php");

export const extractImageSrcsFromHtml = (htmlContent: string): string[] => {
  if (!htmlContent) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, "text/html");
  const images = doc.querySelectorAll("img");

  return Array.from(images)
    .map((img) => normalizeMoodleFileUrl(img.getAttribute("src") || ""))
    .filter((src) => Boolean(src) && isPluginFileUrl(src));
};

export const replaceImageSrcInHtml = (
  htmlContent: string,
  srcMap: Record<string, string>
): string => {
  if (!htmlContent || Object.keys(srcMap).length === 0) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return htmlContent;

  root.querySelectorAll("img").forEach((img) => {
    const originalSrc = img.getAttribute("src") || "";
    const normalizedSrc = normalizeMoodleFileUrl(originalSrc);

    if (!isPluginFileUrl(normalizedSrc)) return;

    const resolvedSrc = srcMap[originalSrc] || srcMap[normalizedSrc];

    if (resolvedSrc) {
      img.setAttribute("src", resolvedSrc);
    }
  });

  return root.innerHTML.trim();
};

const sanitizeHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";

  root.querySelectorAll(".answernumber").forEach((el) => el.remove());

  root.querySelectorAll("*").forEach((el) => {
    el.removeAttribute("class");
    el.removeAttribute("style");
  });

  return root.innerHTML.trim();
};

export function extractQuestionAndAnswers(
  type: "multichoice" | "truefalse" | "essay",
  html: string
): QuizQuestion {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const questionElement = doc.querySelector(".qtext");
  const question = sanitizeHtmlContent(questionElement?.innerHTML || "");
  const questionImageSrcs = extractImageSrcsFromHtml(question);

  const answers: QuizAnswer[] = [];
  let selectedAnswerIndex: number | null = null;

  if (type === "multichoice") {
    const answerLabels = doc.querySelectorAll("[data-region='answer-label']");
    answerLabels.forEach((label, index) => {
      const answerText = sanitizeHtmlContent(label.innerHTML || "");
      const inputId = label.getAttribute("id")?.replace("_label", "") || "";
      const input = inputId
        ? (doc.getElementById(inputId) as HTMLInputElement)
        : null;

      if (input) {
        answers.push({
          text: answerText,
          name: input.name,
          value: input.value,
        });

        if (input.checked) {
          selectedAnswerIndex = index;
        }
      }
    });
  }

  if (type === "truefalse") {
    const answerInputs = doc.querySelectorAll(
      ".answer input[type=radio], .answer input[type=checkbox]"
    );
    answerInputs.forEach((input, index) => {
      const inputEl = input as HTMLInputElement;
      const inputId = inputEl.id;
      const label = inputId ? doc.querySelector(`[for="${inputId}"]`) : null;
      const labelText = sanitizeHtmlContent(label?.innerHTML || "");

      if (inputEl.name && inputEl.value !== "-1") {
        answers.push({
          text: labelText,
          name: inputEl.name,
          value: inputEl.value,
        });

        if (inputEl.checked) {
          selectedAnswerIndex = answers.length - 1;
        }
      }
    });
  }

  if (type === "essay") {
    const textarea = doc.querySelector("textarea");
    if (textarea) {
      answers.push({
        text: textarea.value.trim(),
        name: textarea.name,
        value: textarea.value.trim(),
      });
      selectedAnswerIndex = null;
    }
  }

  const sequenceInput = doc.querySelector(
    "input[type='hidden'][name*=':sequencecheck']"
  ) as HTMLInputElement | null;
  const sequenceCheck = sequenceInput
    ? {
        name: sequenceInput.name,
        value: sequenceInput.value,
      }
    : null;

  return {
    question,
    questionImageSrcs,
    answers,
    selectedAnswerIndex,
    sequenceCheck,
  };
}

export function countQuizPages(layout: string): number {
  if (!layout) return 0;
  const parts = layout.split(",");
  const pageBreaks = parts.filter((p) => p.trim() === "0");
  return pageBreaks.length;
}

type AnswerOption = {
  text: string;
  isSelected: boolean;
  isCorrect: boolean;
};

type QuizReviewResult = {
  question: string;
  questionImageSrcs: string[];
  type: "multichoice" | "truefalse" | "essay";
  status: "correct" | "incorrect" | "unknown";
  selectedAnswer: string | null;
  selectedAnswerIndex: number | null;
  correctAnswer: string | null;
  correctAnswerIndex: number | null;
  answers: AnswerOption[];
  essayResponse?: string | null;
  generalFeedback?: string | null;
};

export function extractQuizReview(
  type: "multichoice" | "truefalse" | "essay",
  html: string
): QuizReviewResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const question = sanitizeHtmlContent(
    doc.querySelector(".qtext")?.innerHTML || ""
  );
  const questionImageSrcs = extractImageSrcsFromHtml(question);

  const queElement = doc.querySelector(".que");
  const status = queElement?.classList.contains("correct")
    ? "correct"
    : queElement?.classList.contains("incorrect")
    ? "incorrect"
    : "unknown";

  const answers: AnswerOption[] = [];
  let selectedAnswer: string | null = null;
  let selectedAnswerIndex: number | null = null;
  let correctAnswer: string | null = null;
  let correctAnswerIndex: number | null = null;
  let essayResponse: string | null = null;
  let generalFeedback: string | null = null;

  if (type === "multichoice" || type === "truefalse") {
    const answerDivs = Array.from(
      doc.querySelectorAll(".answer .r0, .answer .r1")
    );
    answerDivs.forEach((div, index) => {
      const input = div.querySelector(
        'input[type="radio"], input[type="checkbox"]'
      ) as HTMLInputElement;
      const labelDiv =
        div.querySelector('[data-region="answer-label"]') ||
        div.querySelector("label");
      const labelText = sanitizeHtmlContent(labelDiv?.innerHTML || "");
      const isSelected = input?.checked || false;
      const isCorrect = div.classList.contains("correct");

      if (isSelected) {
        selectedAnswer = labelText;
        selectedAnswerIndex = index;
      }

      if (isCorrect) {
        correctAnswer = labelText;
        correctAnswerIndex = index;
      }

      answers.push({
        text: labelText,
        isSelected,
        isCorrect,
      });
    });

    if (correctAnswer === null) {
      const rightAnswerText = doc
        .querySelector(".rightanswer")
        ?.textContent?.trim();
      if (rightAnswerText) {
        const match = rightAnswerText.match(/Câu trả lời đúng là: (.+)/);
        if (match) {
          correctAnswer = match[1].trim();
          correctAnswerIndex = answers.findIndex((a) =>
            a.text.includes(correctAnswer!)
          );
        }
      }
    }
  } else if (type === "essay") {
    const responseDiv = doc.querySelector(".answer");
    essayResponse = sanitizeHtmlContent(responseDiv?.innerHTML || "") || null;
  }

  const generalFeedbackDiv = doc.querySelector(".generalfeedback");
  generalFeedback = sanitizeHtmlContent(generalFeedbackDiv?.innerHTML || "");

  return {
    question,
    questionImageSrcs,
    type,
    status,
    selectedAnswer,
    selectedAnswerIndex,
    correctAnswer,
    correctAnswerIndex,
    answers,
    essayResponse,
    generalFeedback,
  };
}
